import React, { useState, useEffect, useMemo } from 'react'
import { withRouter } from 'react-router-dom'
import PropTypes from 'prop-types'
import cn from 'classnames'
import { get, keys } from 'lodash'
import moment from 'moment'

import RoundButton from '../RoundButton'
import RequestRefundModal from './RequestRefundModal'

import styles from './Details.module.scss'
import SidebarModal from '../SidebarModal'
import { ReactComponent as ErrorIcon } from '../../assets/icons/error.svg'

import { ORDER_STATES, STADIUM_ORDER_STATES, isTabbed } from '../../utils/orderStates'
import { getGroupedItems, getPaymentNames } from '../../utils/orderUtils'
import sumUpModifiers from '../../utils/sumUpPrice'
import PeripheralBridge from '../../utils/peripheralBridge'
import { getNetworkAvailableValue } from '../../utils/networkConnected'
import formatSignedAmount, { getSubtotalAmountInCents } from '../../utils/orderDisplay'
import { isOfflineApproval } from '../../utils/offlineReplayStatuses'
import { useDispatch, useSelector } from 'react-redux'
import { getBackbarMode } from '../../selectors/config'
import BackBarButtons from '../../VNTabs/components/BackBarButtons'
import { getTabsModeEnabled, getUseTaxClasses } from '../../selectors/menus'
import { getCurrentMenuId, getSafMode } from '../../selectors/appState'
import { getDeviceMode } from '../../VNMode/Selectors'
import { MODES } from '../../VNMode/Reducer'
import { getSelectedPromotions } from '../../selectors/promotion'
import { sendCFDScreenNav, sendUpdateCart } from '../../VNAndroidSDK/bridgeCalls/VNWebSDKDataSend'
import {
  setCannotReopenTabDialog,
  setCannotReopenUnsyncedTabDialog,
  setConfirmationDialog,
  setConnectionRequiredDialog,
  setReopenTabDialog,
  setTransferTabModal,
} from '../../VNDialogs/ActionCreators'
import useGetEnvVars from '../../hooks/useGetEnvVars'
import usePrinter from '../../VNPrinters/hooks/usePrinter'
import { APPLICATION_STRINGS } from '../../strings'
import { getReopenedTabPayments } from '../../VNTenders/Selectors'
import VNViewItemDetail from '../../VNItems/components/VNViewItemDetail'
import { useRefundHandler } from '../../VNRefunds/hooks/useRefundHandler'
import TaxRules from './TaxRules'
import { OFFLINE_SAF_MODE } from '../../VNSyncManager/containers/VNSyncManager'
import { CFD_SCREEN } from '../../VNCustomerFacingDisplay/Reducer'
import SplitTabsModal from './SplitTabsModal'
import { isFeatureOn } from '../../VNConfigurations/Selectors'
import { VNCONFIGS_FEATURE_ENABLEMENT_KEYS } from '../../VNConfigurations/Enums'

const ORDER_DETAILS_BUTTONS = {
  SYNC_ORDER: 'Sync Order',
  ADD_ITEMS: 'Add Items',
  VOID_ITEMS: 'Void Items',
  VIEW_REFUND: 'View Refund',
  RECEIPT: 'Receipt',
  PRINT_REFUNDS: 'Print Refunds',
  CLOSE_ORDER: 'Close Order',
  REOPEN_TAB: 'Reopen Tab',
}

const OrderDetails = ({
  history,
  syncOrder,
  onClearOrder,
  voidOrderSucceeded,
  order,
  isKiosk,
  setCartFromTab,
  setOrderInProgressFromTab,
  className,
  clearCart,
  mutationInProgress,
  addNotesToCart,
  addOrderNotesToCart,
  tabsModeEnabled,
}) => {
  const classNames = cn(styles.orderDetails, className)
  const itemModels = get(order, 'itemModels', []).filter((item) => !item.refund)
  const groupedItems = getGroupedItems(order)
  const subtotalInCents = getSubtotalAmountInCents(order)
  const cashRouningInCents = order?.cashRoundingInCents || 0
  const totalInCents = order?.amountInCents - cashRouningInCents
  const roundedTotalInCents = order.amountInCents
  const discountInCents = Math.abs(order?.discountAmountInCents ?? 0)
  const serviceFeeTotal = order?.serviceFee?.total
  const taxInCents = order?.taxAmountInCents
  const tip = order?.tipAmountInCents
  let charity = order?.charityAmountInCents || order?.previousCharityAmountInCents || 0
  charity = order?.previousCharityAmountInCents > 0 && order?.reopened ? order?.previousCharityAmountInCents : charity

  const isMultiPayment = order?.isMultiPayment ?? false
  const [isVoiding, setIsVoiding] = useState(false) // this is always false !?
  const [noBridge, setNoBridge] = useState(false)
  const [noCardTransaction, setNoCardTransaction] = useState(false)
  const [showRefundModal, setShowRefundModal] = useState(false)
  const [showSplitTabModal, setShowSplitTabModal] = useState(false)
  const [showItemDetails, setShowItemDetails] = useState(false)
  const [selectedItem, setSelectedItem] = useState()
  const isTab = isTabbed(order)
  const isTabsMenu = useSelector(getTabsModeEnabled)
  const currentMenuId = useSelector(getCurrentMenuId)
  const backbarMode = useSelector(getBackbarMode)
  const cfdMode = useSelector((state) => getDeviceMode(state))
  const discounts = useSelector((state) => getSelectedPromotions(state))
  const reopenedTabPayments = useSelector((state) => getReopenedTabPayments(state, order.uuid))
  const splitTabsEnabled = useSelector((state) => isFeatureOn(state, VNCONFIGS_FEATURE_ENABLEMENT_KEYS.SPLIT_TABS))
  const transferTabsDisabled = useSelector((state) => isFeatureOn(state, VNCONFIGS_FEATURE_ENABLEMENT_KEYS.DISABLE_TRANSFER_TABS))
  const useTaxClasses = useSelector(getUseTaxClasses)
  const safMode = useSelector(getSafMode)
  const isOfflineOnly = useMemo(() => safMode === OFFLINE_SAF_MODE, [safMode])

  const { isRefundReceiptsEnabled } = useGetEnvVars()

  const { printRefundReceipt } = usePrinter(order.uuid)
  const dispatch = useDispatch()
  const { hasCardPresentRefund } = useRefundHandler()

  const paymentCompleted = useMemo(() => {
    if (backbarMode && isTab) return false

    return order?.paymentCompleted
  }, [backbarMode, isTab, order?.paymentCompleted])

  const networkConnected = getNetworkAvailableValue()

  const isOrderCancelled = useMemo(() => {
    return order.state === STADIUM_ORDER_STATES.CANCELLED
  }, [order])

  const isAuthFailedTab = useMemo(() => {
    if (!isTabsMenu || !order) return false
    // issue with attempting to close auth failed orders from different menus
    if (order.standMenuUuid !== currentMenuId) return false

    // recovering from auth fail, if we void the trigger item, order enters "partially refunded" state
    if (order.state === STADIUM_ORDER_STATES.PARTIALLY_REFUNDED && order.balanceDueInCents > 0) return true

    return order.state === STADIUM_ORDER_STATES.AUTHORIZATION_FAILED
  }, [currentMenuId, isTabsMenu, order])

  const isRefundedOrder = useMemo(() => {
    // issue with attempting to close auth failed orders from different menus
    if (order.standMenuUuid !== currentMenuId) return false

    const refundStates = [
      STADIUM_ORDER_STATES.COMPLETION_PENDING,
      STADIUM_ORDER_STATES.REFUNDED,
      STADIUM_ORDER_STATES.PARTIALLY_REFUNDED,
      STADIUM_ORDER_STATES.CANCELLED,
      STADIUM_ORDER_STATES.APPROVED_OFFLINE,
    ]

    const hasRefundedItems = order?.refundedItemModels?.some((item) => item.refund) || false
    const hasRefundedModifiers =
      order?.refundedItemModels?.some((item) => item.modifiers?.some((modifier) => modifier.refund)) || false

    return refundStates.includes(order.state) && (hasRefundedItems || hasRefundedModifiers)
  }, [currentMenuId, order])

  const isOpenTab = useMemo(() => {
    if (isAuthFailedTab) return true

    return !isKiosk && isTab && !paymentCompleted
  }, [isAuthFailedTab, isKiosk, isTab, paymentCompleted])

  const canAdjustPrice = useMemo(() => {
    if (isKiosk) return false
    if (order.state !== STADIUM_ORDER_STATES.TABBED_OFFLINE && order.state !== STADIUM_ORDER_STATES.TABBED) return false

    return true
  }, [isKiosk, order.state])

  const displayLegacyTax = useMemo(() => taxInCents > 0 && !useTaxClasses, [useTaxClasses, taxInCents])

  const centsToDollars = (cents) => `$${(cents / 100).toFixed(2)}`

  const displayCreated = (created) => {
    const date = moment(created).format('M/D/YY')
    const time = moment(created).format('h:mm a')

    return date + ', ' + time
  }

  const getNotes = (itemUuid) => {
    const item = order.orderMenuItems?.find((item) => item.uuid === itemUuid)
    return item?.notes || ''
  }

  const syncUnsyncedOrder = () => {
    order.unsyncable = false
    if (!order.id && !order.uuid) return
    syncOrder(order.id || order.uuid, order)
  }

  useEffect(() => {
    if (hasCardPresentRefund()) return
    setIsVoiding(false)
    setNoBridge(false)
    setNoCardTransaction(false)

    const bridgeInstance = PeripheralBridge.getBridge()
    if (!bridgeInstance) return

    bridgeInstance.registerHandler('handleResult', function (data, callback) {
      if (cfdMode === MODES.POS) return
      voidOrderSucceeded(order.id || order.uuid, data)
      setIsVoiding(false)
    })

    bridgeInstance.registerHandler('voidTransaction', function (data, callback) {
      voidOrderSucceeded(order.id || order.uuid, data)
      setIsVoiding(false)
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cfdMode, order])

  const isOfflineCardApproval = isOfflineApproval(order)
  const replayAuthDidFail = () => isOfflineCardApproval && keys(order.replayStatus).length > 0
  const voidDidSucceed = () => !!order.voidedAt
  const voidDidFail = () => !!order.voidFailedAt
  const mediaQueryMobile = window?.matchMedia('(max-width: 500px)')
  const refundButtonText = mediaQueryMobile.matches ? 'Void' : 'Void Items'
  const paymentTitle = tabsModeEnabled ? 'Tender/Name:' : 'Payment:'

  useEffect(() => {
    if (cfdMode !== MODES.POS) return
    if (!isTab) return

    sendCFDScreenNav(CFD_SCREEN.CART)
    sendUpdateCart(order?.itemModels, discounts)
  }, [cfdMode, discounts, isTab, order?.itemModels])

  useEffect(() => {
    return () => {
      dispatch(setConfirmationDialog(null))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const makeDetailsButton = (text, disabled, onClick) => {
    return (
      <RoundButton
        className={styles.actionButton}
        text={text}
        key={text}
        disabled={mutationInProgress || disabled}
        onClick={onClick}
      />
    )
  }

  /// Add Items Button
  const AddItemsButton = () => {
    if (isAuthFailedTab) return null

    return makeDetailsButton(ORDER_DETAILS_BUTTONS.ADD_ITEMS, false, () => {
      if (!networkConnected && order?.wasCreatedInStadium) {
        dispatch(setConnectionRequiredDialog({ open: true }))
        return
      }
      setOrderInProgressFromTab(order)
      clearCart()
      addNotesToCart(order.userNotes?.split('\nNote: ')[0])
      order.userNotes?.split('\nNote: ')[1] && addOrderNotesToCart(`Note: ${order.userNotes?.split('\nNote: ')[1]}`)
      history.push('/concession-order')
    })
  }

  /// Void Items Button
  const VoidItemsButton = () => {
    if (!isOpenTab) return null
    if (isVoiding) return null
    if (noBridge) return null // this is always false ?
    if (noCardTransaction) return null // this is always false ?

    return makeDetailsButton(ORDER_DETAILS_BUTTONS.VOID_ITEMS, false, () => {
      if (!networkConnected && order?.wasCreatedInStadium) {
        dispatch(setConnectionRequiredDialog({ open: true }))
        return
      }

      if (isOfflineCardApproval) {
        dispatch(setConfirmationDialog(true, APPLICATION_STRINGS.ORDER_CANNOT_VOID, null, 'OK'))
        return
      }

      setOrderInProgressFromTab(order)
      setShowRefundModal(true)
    })
  }

  /// Close Order Button
  const CloseOrderButton = () => {
    if (!isOpenTab || backbarMode) {
      return null
    }
    return makeDetailsButton(ORDER_DETAILS_BUTTONS.CLOSE_ORDER, false, () => {
      if (!networkConnected && order?.wasCreatedInStadium) {
        dispatch(setConnectionRequiredDialog({ open: true }))
        return
      }

      setOrderInProgressFromTab(order)

      history.push('/tender-selection')
    })
  }

  /// View Refund Button
  const ViewRefundButton = () => {
    if (isOpenTab) return null

    return makeDetailsButton(order.refundable ? ORDER_DETAILS_BUTTONS.VIEW_REFUND : refundButtonText, false, () => {
      if (!networkConnected && order?.wasCreatedInStadium) {
        dispatch(setConnectionRequiredDialog({ open: true }))
        return
      }

      if (hasCardPresentRefund() && isOfflineOnly) {
        dispatch(setConnectionRequiredDialog({ open: true }))
        return
      }

      if (isOfflineCardApproval) {
        dispatch(setConfirmationDialog(true, APPLICATION_STRINGS.ORDER_CANNOT_VOID, null, 'OK'))
        return
      }

      if (order?.state === STADIUM_ORDER_STATES.TABBED_OFFLINE || order?.state === STADIUM_ORDER_STATES.APPROVED_OFFLINE) {
        dispatch(setConfirmationDialog(true, APPLICATION_STRINGS.ORDER_CANNOT_VOID, null, 'OK'))
        return
      }

      setShowRefundModal(true)
    })
  }

  /// Print Refunds Button
  const PrintRefundsButton = () => {
    if (!isRefundedOrder) return null
    if (!isRefundReceiptsEnabled) return null

    return makeDetailsButton(ORDER_DETAILS_BUTTONS.PRINT_REFUNDS, false, () => {
      printRefundReceipt()
    })
  }

  /// View Receipt Button
  const ViewReceiptButton = () => {
    if (isAuthFailedTab) return null
    if (isKiosk) return null
    if (isTab && !paymentCompleted) return null

    return makeDetailsButton(ORDER_DETAILS_BUTTONS.RECEIPT, false, () => {
      history.push(`receipt/${order.uuid}?origin=orderDetails`)
    })
  }

  const SplitTabButton = () => {
    if (!splitTabsEnabled) return null
    if (isAuthFailedTab) return null
    if (!isOpenTab) return null
    if (isKiosk) return null
    if (paymentCompleted) return null
    if (!isTab) return null
    if (isOrderCancelled) return null

    return makeDetailsButton('Split Tab', false, () => {
      if (!networkConnected && order?.wasCreatedInStadium) {
        dispatch(setConnectionRequiredDialog({ open: true, subtext: APPLICATION_STRINGS.CANNOT_SPLIT_TAB_WHILE_OFFLINE }))
        return
      }

      setOrderInProgressFromTab(order)
      setShowSplitTabModal(true)
    })
  }

  const TransferTabsButton = () => {
    if (transferTabsDisabled) return null
    if (isAuthFailedTab) return null
    if (!isOpenTab) return null
    if (isKiosk) return null
    if (paymentCompleted) return null
    if (!isTab) return null
    if (isOrderCancelled) return null

    return makeDetailsButton(APPLICATION_STRINGS.TRANSFER_TABS_BUTTON_NAME, false, () => {
      if (!networkConnected && order?.wasCreatedInStadium) {
        dispatch(setConnectionRequiredDialog({ open: true, subtext: APPLICATION_STRINGS.CANNOT_TRANSFER_TAB_WHILE_OFFLINE }))
        return
      }

      setOrderInProgressFromTab(order)
      dispatch(
        setTransferTabModal({
          open: true,
          order: {
            uuid: order.uuid,
            wasCreatedInStadium: order.wasCreatedInStadium,
            employeeId: order.employeeId,
            orderNumber: order.orderNumber,
          },
        }),
      )
    })
  }

  /// Sync Order Button
  const SyncOrderButton = () => {
    if (order?.syncedAt) return null
    if (!order?.wasCreatedInStadium) return null

    return makeDetailsButton(ORDER_DETAILS_BUTTONS.SYNC_ORDER, false, () => {
      if (!networkConnected) {
        dispatch(setConnectionRequiredDialog({ open: true }))
        return
      }
      syncUnsyncedOrder()
    })
  }

  /// Reopen tab Button
  const ReopenTabButton = () => {
    if (!isTabsMenu) return null
    if (!order.isTab) return null
    if (isOpenTab) return null

    return makeDetailsButton(ORDER_DETAILS_BUTTONS.REOPEN_TAB, false, () => {
      if (!networkConnected) {
        dispatch(setConnectionRequiredDialog({ open: true, subtext: APPLICATION_STRINGS.CANNOT_REOPEN_WHILE_OFFLINE }))
        return
      }

      //Device is offline but oder is not synced
      if (!order?.wasCreatedInStadium) {
        dispatch(setCannotReopenUnsyncedTabDialog({ open: true }))
        return
      }

      //TODO: validate tabs fully closed offline criteria
      const isFullyClosedOfflineTab = order.state === STADIUM_ORDER_STATES.COMPLETED && order.isTab && !order.wasCreatedInStadium
      const isOrderAbleToReopen =
        order.state === STADIUM_ORDER_STATES.COMPLETION_PENDING || order.state === ORDER_STATES.SETTLEMENT_FAILED

      if (!isOrderAbleToReopen && !isFullyClosedOfflineTab) {
        dispatch(setCannotReopenTabDialog({ open: true }))
        return
      }

      dispatch(setReopenTabDialog({ open: true, isFullyClosedOfflineTab }))
    })
  }

  const handleItemClick = (item) => {
    if (!canAdjustPrice) return

    setShowItemDetails(true)
    setSelectedItem(item)
  }

  return (
    <SidebarModal show title="Order Details" onClose={onClearOrder} className={classNames}>
      {showRefundModal && <RequestRefundModal order={order} edit={!order.refundable} onClose={() => setShowRefundModal(false)} />}
      {showSplitTabModal && <SplitTabsModal order={order} edit={!order.refundable} onClose={() => setShowSplitTabModal(false)} />}
      {showItemDetails && (
        <VNViewItemDetail
          itemUuid={selectedItem.id}
          selectedModifiers={selectedItem.modifiers}
          notes={getNotes(selectedItem.lineItemUuid)}
          onClose={() => setShowItemDetails(false)}
          quantity={selectedItem.quantity}
          priceInCents={selectedItem.priceInCents}
          originalPriceInCents={selectedItem.originalPriceInCents}
          name={selectedItem.name}
          orderUuid={order.uuid}
          lineItemUuids={selectedItem.lineItemUuids}
          wasCreatedInStadium={order.wasCreatedInStadium}
        />
      )}
      <div className={styles.bodyContainer}>
        <div className={styles.scrollable}>
          <div className={styles.metadata}>
            <div className={styles.row}>
              <span>{`${order.revenueName ?? ''} Order #${order.orderNumber}`}</span>
            </div>

            <div className={styles.row}>
              <span>
                {paymentTitle} {isMultiPayment ? 'Split Pay' : getPaymentNames(order, reopenedTabPayments)}
              </span>
            </div>

            <div className={styles.row}>
              <span>Created: {displayCreated(order?.submittedAt)}</span>
            </div>

            <div className={styles.row}>
              <span>Synced: {order.syncedAt ? displayCreated(order.syncedAt) : '-'}</span>
            </div>

            <div className={styles.row}>
              <span>Subtotal: {centsToDollars(subtotalInCents)}</span>
            </div>

            {order?.userNotes?.split('\nNote:')[0] && (
              <div className={styles.row}>
                <span>Location: {order?.userNotes?.split('\nNote:')[0]}</span>
              </div>
            )}

            {order?.userNotes?.split('\nNote:')[1] && (
              <div className={styles.row}>
                <span className={styles.multiLine}>Note: {order?.userNotes?.split('\nNote:')[1]}</span>
              </div>
            )}
          </div>

          <div className={styles.lineItems}>
            {groupedItems.map((item, idx) => {
              const name = item.name
              const modifierString = item.modifiersString
              const modifiers = item.modifiers
              const quantity = item.quantity
              const price = centsToDollars(item.priceInCents + sumUpModifiers(modifiers))

              if (item.type === 'DonationLineItem') return null // Skip donation items

              return (
                <div className={styles.item} key={idx} onClick={() => handleItemClick(item)}>
                  <div className={styles.column}>
                    <span className={styles.quantity}>{`${quantity}`}</span>
                  </div>
                  <div className={styles.column}>
                    <span className={modifierString ? styles.name : styles.nameWithoutMod}>{`${name}`}</span>
                    <span className={styles.modifiers}>{`${modifierString}`}</span>
                  </div>
                  <div className={styles.column}>
                    <span className={styles.price}>{`${price}`}</span>
                  </div>
                </div>
              )
            })}
          </div>

          <div className={styles.total}>
            <div className={styles.row}>
              <span>{`Subtotal (${itemModels.length})`}</span>
              <span className={styles.totalRow}>{centsToDollars(subtotalInCents)}</span>
            </div>
            {discountInCents !== 0 && (
              <div className={styles.row}>
                <span className={styles.discountRow}>Discount</span>
                <span className={cn(styles.totalRow, styles.discountRow)}>- {centsToDollars(discountInCents)}</span>
              </div>
            )}
            {serviceFeeTotal > 0 && (
              <div className={styles.row}>
                <span>{order?.serviceFee?.name ?? 'Administrative Charge'}</span>
                <span className={styles.totalRow}>{centsToDollars(serviceFeeTotal)}</span>
              </div>
            )}

            {displayLegacyTax && (
              <div className={styles.row}>
                <span>Sales Tax</span>
                <span className={styles.totalRow}>{centsToDollars(taxInCents)}</span>
              </div>
            )}
            {useTaxClasses && (
              <TaxRules taxRulesBreakdown={order.taxRulesBreakdown} rowStyle={styles.row} totalStyle={styles.totalRow}></TaxRules>
            )}
            {order?.taxExempt && (
              <div className={styles.row}>
                <span>Tax</span>
                <span className={styles.totalRow}>Exempt</span>
              </div>
            )}
            {tip > 0 && (
              <div className={styles.row}>
                <span>Gratuity</span>
                <span className={styles.totalRow}>{centsToDollars(tip)}</span>
              </div>
            )}
            {charity > 0 && (
              <div className={styles.row}>
                <span>Donation</span>
                <span className={styles.totalRow}>{centsToDollars(charity)}</span>
              </div>
            )}
            <div className={styles.row}>
              <span>Total</span>
              <span className={styles.totalRow}>{centsToDollars(totalInCents)}</span>
            </div>
            {order?.cashRounded && (
              <>
                <div className={styles.row}>
                  <span>Penny Rounding</span>
                  <span className={styles.totalRow}>{formatSignedAmount(cashRouningInCents)}</span>
                </div>
                <div className={styles.row}>
                  <span>Rounded Total</span>
                  <span className={styles.totalRow}>{centsToDollars(roundedTotalInCents)}</span>
                </div>
              </>
            )}
          </div>

          <div className={styles.orderHistoryContainer} style={!order.errorMessage ? { borderTop: '0' } : null}>
            {isOfflineCardApproval && (
              <div className={styles.row}>
                <span className={styles.errorMessage}>Credit card approved offline</span>
              </div>
            )}

            {replayAuthDidFail() && (
              <div className={styles.row}>
                <span className={styles.errorMessage}>{`${order?.replayStatus?.statusDisplay}`}</span>
              </div>
            )}

            {voidDidSucceed() && (
              <div className={styles.row}>
                <span className={styles.errorMessage}>{`Voided at ${displayCreated(order.voidedAt)}`}</span>
              </div>
            )}

            {voidDidFail() && (
              <div className={styles.row}>
                <span className={styles.errorMessage}>{`Unsuccessful void attempt at ${displayCreated(
                  order.voidFailedAt,
                )}`}</span>
              </div>
            )}

            {order.errorMessage && (
              <div className={styles.row}>
                <div className={styles.column}>
                  <ErrorIcon />
                </div>

                <div className={styles.column}>
                  <span className={styles.errorMessage}>{`${order.errorMessage}`}</span>
                </div>
              </div>
            )}
          </div>
        </div>
        <div className={styles.buttonsContainer}>
          {isOpenTab && <AddItemsButton />}
          <SplitTabButton />
          <ReopenTabButton />
          <VoidItemsButton />
          <ViewRefundButton />
          <ViewReceiptButton />
          <PrintRefundsButton />
          <BackBarButtons className={cn(styles.actionButton)} />
          <TransferTabsButton />
          <CloseOrderButton />
          <SyncOrderButton />
        </div>
      </div>
    </SidebarModal>
  )
}

OrderDetails.defaultProps = {
  order: {},
}

OrderDetails.propTypes = {
  syncOrder: PropTypes.func,
  onCloseDetails: PropTypes.func,
  voidOrderSucceeded: PropTypes.func,
  setCartFromTab: PropTypes.func,
  setOrderInProgressFromTab: PropTypes.func,
  order: PropTypes.object,
  tabsModeEnabled: PropTypes.bool,
}

export default withRouter(OrderDetails)
